// =====================================================================
// Calendar (Sales) — two-week personal view
//
// Aggregates everything the current user (or a selected rep) is on the
// hook for across the next ~14 days: tasks due, activities logged or
// scheduled (calls / emails / meetings / generic tasks), research
// interviews where they are the interviewer, and lead recall dates
// pending against nurtured leads they own.
//
// Read-only — clicks navigate to the source record. Coloured pills make
// each cell scan quickly; today's column gets a subtle accent.
//
// Split out from sales-misc.jsx so each page lives in its own file.
// Duplicates the small `midnight` / `startOfWeek` helpers rather than
// adding cross-file coupling — they are six lines combined.
// =====================================================================

function _cal_midnight(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function _cal_startOfWeek(d) {
  // ISO week: Monday is day 1. Treat Sunday (getDay()=0) as 7 so it lands
  // at the end of the previous week, not the start of a new one.
  const x = _cal_midnight(d);
  const day = x.getDay() === 0 ? 7 : x.getDay();
  x.setDate(x.getDate() - (day - 1));
  return x;
}

// Pill tone per item kind. `color` is the literal CSS value used for the
// left-border accent and the kind label; `tone` is the Badge tone name
// for any badges that consume the same kind elsewhere. Interview gets a
// hard-coded violet because the existing design tokens don't include a
// purple — every other kind reuses a `var(--…)` token.
const CAL_KIND_META = {
  task:      { label: "Task",        color: "var(--accent)", tone: "amber" },
  call:      { label: "Call",        color: "var(--green)",  tone: "green" },
  email:     { label: "Email",       color: "var(--slate)",  tone: "slate" },
  meeting:   { label: "Meeting",     color: "var(--blue)",   tone: "blue"  },
  interview: { label: "Interview",   color: "#7c3aed",       tone: "slate" },
  recall:    { label: "Lead recall", color: "var(--red)",    tone: "red"   },
};

// Map an Activity row's `type` (Call / Email / Meeting / Task) to one of
// our pill kinds. Anything else falls through to a generic "task" pill
// so unknown future activity types still render.
function _cal_activityKind(type) {
  switch ((type || "").toLowerCase()) {
    case "call":    return "call";
    case "email":   return "email";
    case "meeting": return "meeting";
    case "task":    return "task";
    default:        return "task";
  }
}

// Build the route for an item so clicking it lands on the source row.
function _cal_itemRouteFromIds({ leadId, oppId, contactId, accountId, interviewId }) {
  if (interviewId)        return "research/interviews/" + interviewId;
  if (oppId)              return "sales/opportunities/" + oppId;
  if (leadId)             return "sales/leads/" + leadId;
  if (contactId)          return "sales/contacts/" + contactId;
  if (accountId)          return "sales/accounts/" + accountId;
  return null;
}

function CalendarPage({ navigate }) {
  const { user } = useAuth();

  // Map the logged-in auth user to a sales-rep row by email. Falls back
  // to "all reps" when there's no matching rep (e.g. admin login).
  const myRep = useMemo(
    () => (D.USERS || []).find(u => u.email && user && u.email.toLowerCase() === (user.email || "").toLowerCase()) || null,
    [user]
  );

  const [ownerFilter, setOwnerFilter] = useState(myRep ? myRep.id : "all");
  const [weekOffset, setWeekOffset]   = useState(0); // 0 = current ISO week, +1 = next 2-week pair, etc.
  // Kind filter: Set of enabled item kinds. Default = all. Clicking a
  // chip in the legend toggles its inclusion. We never let the set go
  // empty (would render an empty calendar with no signal) — the last
  // active chip stays sticky.
  const [enabledKinds, setEnabledKinds] = useState(() => new Set(Object.keys(CAL_KIND_META)));
  function toggleKind(k) {
    setEnabledKinds(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      if (next.size === 0) next.add(k);  // sticky: keep at least one kind
      return next;
    });
  }
  function showAllKinds() {
    setEnabledKinds(new Set(Object.keys(CAL_KIND_META)));
  }
  const allKindsOn = enabledKinds.size === Object.keys(CAL_KIND_META).length;

  // Window is two ISO weeks starting Monday. We let weekOffset slide the
  // whole pair by two weeks at a time so each press of next/prev shows a
  // fresh, non-overlapping fortnight.
  const windowStart = useMemo(() => {
    const s = _cal_startOfWeek(new Date());
    s.setDate(s.getDate() + weekOffset * 14);
    return s;
  }, [weekOffset]);
  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date(windowStart);
      d.setDate(d.getDate() + i);
      out.push(d);
    }
    return out;
  }, [windowStart]);
  const windowStartIso = days[0].toISOString().slice(0, 10);
  const windowEndIso   = days[13].toISOString().slice(0, 10);
  const todayIso       = _cal_midnight(new Date()).toISOString().slice(0, 10);

  // Collect everything in range, then group by ISO date string. Each
  // source is filtered by ownership against the selected rep (or pass-
  // through when "all").
  const items = useMemo(() => {
    const out = [];
    const ownerMatch = (val) => ownerFilter === "all" || val === ownerFilter;

    // 1. Tasks with dueDate inside the window — skip completed/cancelled.
    for (const t of (D.TASKS || [])) {
      if (!t.dueDate) continue;
      if (t.dueDate < windowStartIso || t.dueDate > windowEndIso) continue;
      if (t.status === "Done" || t.status === "Cancelled") continue;
      if (!ownerMatch(t.owner)) continue;
      out.push({
        id: "task:" + t.id,
        date: t.dueDate,
        kind: "task",
        title: t.title || "Task",
        route: _cal_itemRouteFromIds({ leadId: t.leadId, oppId: t.oppId, contactId: t.contactId, accountId: t.accountId }),
      });
    }

    // 2. Activities (call/email/meeting/task) within the window. Cancelled
    //    activities and notes-without-date fall out naturally.
    for (const a of (D.ACTIVITIES || [])) {
      if (!a.date) continue;
      if (a.date < windowStartIso || a.date > windowEndIso) continue;
      if (!ownerMatch(a.owner)) continue;
      out.push({
        id: "act:" + a.id,
        date: a.date,
        kind: _cal_activityKind(a.type),
        title: a.subject || a.type || "Activity",
        route: _cal_itemRouteFromIds({ oppId: a.oppId, contactId: a.contactId, accountId: a.accountId }),
      });
    }

    // 3. Interviews where the selected rep is the interviewer. Both
    //    Scheduled and Completed are shown so the user can scan recent
    //    history alongside upcoming sessions.
    for (const iv of (D.INTERVIEWS || [])) {
      if (!iv.date) continue;
      if (iv.date < windowStartIso || iv.date > windowEndIso) continue;
      if (!ownerMatch(iv.interviewer)) continue;
      const contact = getContact(iv.contactId);
      const who = contact ? `${contact.first} ${contact.last}` : (iv.type || "Interview");
      out.push({
        id: "iv:" + iv.id,
        date: iv.date,
        kind: "interview",
        title: who,
        route: "research/interviews/" + iv.id,
      });
    }

    // 4. Nurtured-lead recall dates — these are the silent reminders the
    //    rep set when parking the lead. They aren't always backed by a
    //    follow-up task (the rep may have unticked it), so we surface
    //    them here directly off the lead row.
    for (const l of (D.LEADS || [])) {
      if (l.status !== "Nurture" || !l.recallDate) continue;
      if (l.recallDate < windowStartIso || l.recallDate > windowEndIso) continue;
      if (!ownerMatch(l.owner)) continue;
      out.push({
        id: "recall:" + l.id,
        date: l.recallDate,
        kind: "recall",
        title: `${l.first} ${l.last} · ${l.company || ""}`.trim(),
        route: "sales/leads/" + l.id,
      });
    }

    return out;
  }, [ownerFilter, windowStartIso, windowEndIso]);

  // Group by yyyy-mm-dd so each cell can splice its slice cheaply.
  // Filtered through enabledKinds so disabled chips drop their items.
  const itemsByDate = useMemo(() => {
    const g = {};
    for (const it of items) {
      if (!enabledKinds.has(it.kind)) continue;
      (g[it.date] || (g[it.date] = [])).push(it);
    }
    // Stable order within a cell: by kind (Meeting/Call/Email/Task/Interview/Recall) then title.
    const order = ["meeting", "call", "email", "interview", "recall", "task"];
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => {
        const ka = order.indexOf(a.kind), kb = order.indexOf(b.kind);
        if (ka !== kb) return ka - kb;
        return (a.title || "").localeCompare(b.title || "");
      });
    }
    return g;
  }, [items, enabledKinds]);

  // Visible total counts only the kinds the user hasn't filtered out, so
  // the sub-text and empty-state messaging reflects what's actually on
  // screen rather than the unfiltered superset.
  const visibleCount = useMemo(
    () => Object.values(itemsByDate).reduce((n, arr) => n + arr.length, 0),
    [itemsByDate]
  );

  const reps = assignableReps();
  const totalCount  = items.length;
  const selectedRep = reps.find(r => r.id === ownerFilter);
  // Sub-text reflects the visible count when a kind filter is on, the
  // total otherwise. Keeps the framing honest: "12 visible (of 18)" vs.
  // "18 items".
  const sub = (allKindsOn
        ? `${totalCount} item${totalCount === 1 ? "" : "s"}`
        : `${visibleCount} visible (of ${totalCount})`)
    + " · "
    + (ownerFilter === "all"
        ? "all reps"
        : (selectedRep ? selectedRep.name : ownerFilter))
    + " · " + fmtDateShort(days[0]) + " — " + fmtDateShort(days[13])
    + " · your work for the fortnight";

  // Compact pill — fixed height so vertical stacks line up across cells.
  function ItemPill({ it }) {
    const meta = CAL_KIND_META[it.kind] || CAL_KIND_META.task;
    return (
      <div
        onClick={() => it.route && navigate(it.route)}
        title={`${meta.label} · ${it.title}`}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "3px 6px", borderRadius: 4,
          background: "var(--bg-sunken)",
          borderLeft: `3px solid ${meta.color}`,
          fontSize: 11.5, lineHeight: 1.3,
          cursor: it.route ? "pointer" : "default",
          marginBottom: 3,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}
      >
        <span style={{ fontWeight: 600, color: meta.color, fontSize: 10, textTransform: "uppercase", letterSpacing: ".04em" }}>
          {meta.label.slice(0, 4)}
        </span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{it.title}</span>
      </div>
    );
  }

  function DayCell({ d }) {
    const iso = d.toISOString().slice(0, 10);
    const isToday   = iso === todayIso;
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    const list = itemsByDate[iso] || [];
    return (
      <div style={{
        background: "var(--bg-elev)",
        borderRadius: 8,
        border: `1px solid ${isToday ? "var(--accent)" : "var(--border)"}`,
        padding: 8, minHeight: 130,
        boxShadow: isToday ? "0 0 0 2px var(--accent-soft) inset" : "none",
        opacity: isWeekend && list.length === 0 ? 0.7 : 1,
        display: "flex", flexDirection: "column",
      }}>
        <div style={{
          display: "flex", alignItems: "baseline", justifyContent: "space-between",
          marginBottom: 6, paddingBottom: 4,
          borderBottom: "1px solid var(--divider)",
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: isToday ? "var(--accent-text)" : "var(--text-muted)", letterSpacing: ".05em" }}>
              {d.toLocaleDateString(undefined, { weekday: "short" })}
            </span>
            <span style={{ fontSize: 16, fontWeight: 700, color: isToday ? "var(--accent-text)" : "var(--text)" }}>
              {d.getDate()}
            </span>
          </div>
          {list.length > 0 && (
            <span className="muted" style={{ fontSize: 10.5 }}>{list.length}</span>
          )}
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          {list.length === 0 ? (
            <div className="muted" style={{ fontSize: 11, fontStyle: "italic" }}>—</div>
          ) : (
            list.map(it => <ItemPill key={it.id} it={it} />)
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHead
        title="Calendar"
        sub={sub}
        actions={
          <>
            <button className="btn btn-sm" onClick={() => setWeekOffset(o => o - 1)}>← Prev</button>
            <button className="btn btn-sm" onClick={() => setWeekOffset(0)} disabled={weekOffset === 0}>Today</button>
            <button className="btn btn-sm" onClick={() => setWeekOffset(o => o + 1)}>Next →</button>
            <select className="btn btn-sm" value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
              <option value="all">All reps</option>
              {myRep && <option value={myRep.id}>Me ({myRep.name})</option>}
              {reps.filter(r => r.id !== myRep?.id).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </>
        }
      />

      {/* Kind filter — also doubles as the legend. Click a chip to
          show/hide that kind; the swatch acts as both colour key and
          active indicator. */}
      <div className="card" style={{ padding: "8px 16px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span className="muted" style={{ fontSize: 12 }}>Show:</span>
        {Object.entries(CAL_KIND_META).map(([k, meta]) => {
          const on = enabledKinds.has(k);
          return (
            <button
              key={k}
              type="button"
              onClick={() => toggleKind(k)}
              title={on ? `Hide ${meta.label}` : `Show ${meta.label}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "3px 10px", borderRadius: 999,
                border: `1px solid ${on ? meta.color : "var(--border)"}`,
                background: on ? "var(--bg-elev)" : "var(--bg-sunken)",
                color: on ? "var(--text)" : "var(--text-muted)",
                fontSize: 12,
                cursor: "pointer",
                opacity: on ? 1 : 0.6,
              }}
            >
              <span style={{
                display: "inline-block", width: 10, height: 10, borderRadius: 2,
                background: meta.color,
                opacity: on ? 1 : 0.4,
              }} />
              {meta.label}
            </button>
          );
        })}
        {!allKindsOn && (
          <button type="button" className="btn btn-sm"
                  style={{ marginLeft: 4 }}
                  onClick={showAllKinds}>
            Show all
          </button>
        )}
      </div>

      {/* Two weeks × seven days */}
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        {[0, 7].map(weekStart => (
          <div key={weekStart} style={{
            display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8,
          }}>
            {days.slice(weekStart, weekStart + 7).map(d => (
              <DayCell key={d.toISOString()} d={d} />
            ))}
          </div>
        ))}
      </div>

      {visibleCount === 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="card-body muted">
            {totalCount === 0
              ? <>Nothing scheduled in this fortnight for {ownerFilter === "all" ? "any rep" : (selectedRep?.name || "this rep")}.
                Log a call / email / meeting on any account, contact or opportunity to populate this view.</>
              : <>No items match the current chip filter. {!allKindsOn && <a className="tbl-link" onClick={showAllKinds}>Show all</a>}</>}
          </div>
        </div>
      )}
    </>
  );
}

window.CalendarPage = CalendarPage;
